import { ChangeEvent, FC, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { type AxiosError } from "axios";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import $api from "../../shared/api/axios";
import { z } from "zod";
import { FigmaTypography } from "@libs/zapi-ui/custom";

const CredentialsSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1)
});

type TCredentials = z.infer<typeof CredentialsSchema>;

export const LoginPage: FC = () => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState<TCredentials>({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const { search } = useLocation();

  const isValidCredentials = CredentialsSchema.safeParse(credentials).success;

  const mutation = useMutation({
    mutationFn: async (credentials: TCredentials) => {
      const payload = { email: credentials.email, password: credentials.password };
      return await $api.post<{ data: { requiresTwoFactor?: boolean; twoFactorToken?: string } }>(
        "/auth/login",
        payload
      );
    },
    onSuccess: (response) => {
      const data = response.data?.data;

      if (data?.requiresTwoFactor && data?.twoFactorToken) {
        const next = new URLSearchParams(search).get("next");
        const otpUrl = next
          ? `/verify-otp?token=${data.twoFactorToken}&next=${encodeURIComponent(next)}`
          : `/verify-otp?token=${data.twoFactorToken}`;
        navigate(otpUrl);
        return;
      }

      const next = new URLSearchParams(search).get("next");

      if (next) {
        navigate(next);
        return;
      }
      navigate("/dashboard");
    },
    onError: (err: AxiosError<{ message: string }>) => {
      const message = err?.response?.data?.message ?? "Failed to login";
      toast.error(message);
    }
  });

  async function handleLogin() {
    if (!isValidCredentials) return;
    try {
      await mutation.mutateAsync(credentials);
    } catch {
      // Error is handled in onError
    }
  }

  function handleInputChange(e: ChangeEvent<HTMLInputElement>, field: keyof TCredentials) {
    setCredentials((prev) => ({ ...prev, [field]: e.target.value.trim() }));
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background">
      <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
        <div className="flex flex-col space-y-2 text-center">
          <FigmaTypography component="h1" variant="memo-heading-1">Admin Login</FigmaTypography>
          <p className="text-[14px] text-muted-foreground">Enter your credentials to access the admin panel</p>
        </div>

        <div className="grid gap-4">
          <div className="grid gap-2">
            <label htmlFor="email" className="text-[14px] font-medium leading-none">
              Email
            </label>
            <input
              id="email"
              type="email"
              className="h-10 rounded-md border border-input bg-background px-3 py-2 text-[14px] ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              value={credentials.email}
              onChange={(e) => handleInputChange(e, "email")}
            />
          </div>
          <div className="grid gap-2">
            <label htmlFor="password" className="text-[14px] font-medium leading-none">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-[14px] ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                value={credentials.password}
                onChange={(e) => handleInputChange(e, "password")}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[14px] text-muted-foreground"
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>
          <button
            className={`h-10 rounded-md bg-primary px-4 py-2 text-[14px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 ${
              !isValidCredentials || mutation.isPending ? "cursor-not-allowed opacity-70" : ""
            }`}
            disabled={!isValidCredentials || mutation.isPending}
            onClick={handleLogin}
          >
            {mutation.isPending ? "Logging in..." : "Login"}
          </button>
        </div>
      </div>
    </div>
  );
};
